import React, { useState, useEffect } from 'react';
import { Settings, Save, Palette, Globe, Bell, Loader2, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { settingsApi } from '../../services/api';

const AdminSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await settingsApi.get();
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSocialChange = (platform, value) => {
    setSettings({
      ...settings,
      socialLinks: { ...settings.socialLinks, [platform]: value }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update(settings);
      alert('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración</h1>
          <p className="text-gray-500">Ajustes generales del sitio</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#50c878] hover:bg-[#40a0a0]">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Guardar Cambios
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#50c878]" />
              Configuración General
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="siteName">Nombre del Sitio</Label>
              <Input
                id="siteName"
                value={settings?.siteName || ''}
                onChange={(e) => handleChange('siteName', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="language">Idioma</Label>
              <Select 
                value={settings?.language || 'es'} 
                onValueChange={(value) => handleChange('language', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="es">Español</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="fr">Français</SelectItem>
                  <SelectItem value="de">Deutsch</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="footerText">Texto del Footer</Label>
              <Input
                id="footerText"
                value={settings?.footerText || ''}
                onChange={(e) => handleChange('footerText', e.target.value)}
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-[#50c878]" />
              Apariencia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="primaryColor">Color Principal</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="primaryColor"
                  type="color"
                  value={settings?.primaryColor || '#50c878'}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={settings?.primaryColor || ''}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="secondaryColor">Color Secundario</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="secondaryColor"
                  type="color"
                  value={settings?.secondaryColor || '#c9a227'}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={settings?.secondaryColor || ''}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="backgroundColor">Color de Fondo</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  id="backgroundColor"
                  type="color"
                  value={settings?.backgroundColor || '#1a1a1a'}
                  onChange={(e) => handleChange('backgroundColor', e.target.value)}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={settings?.backgroundColor || ''}
                  onChange={(e) => handleChange('backgroundColor', e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Features */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#50c878]" />
              Funcionalidades
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Mostrar Trailer al Cargar</Label>
                <p className="text-sm text-gray-500">Reproduce el trailer automáticamente</p>
              </div>
              <Switch
                checked={settings?.showTrailerOnLoad || false}
                onCheckedChange={(checked) => handleChange('showTrailerOnLoad', checked)}
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Habilitar Notificaciones</Label>
                <p className="text-sm text-gray-500">Notificaciones de nuevas sesiones</p>
              </div>
              <Switch
                checked={settings?.enableNotifications || false}
                onCheckedChange={(checked) => handleChange('enableNotifications', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Meta Pixel / Tracking */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#50c878]" />
              Meta Pixel (Facebook)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="metaPixelId">ID del Pixel de Meta</Label>
              <Input
                id="metaPixelId"
                value={settings?.metaPixelId || ''}
                onChange={(e) => handleChange('metaPixelId', e.target.value)}
                placeholder="123456789012345"
                className="mt-1"
                data-testid="meta-pixel-id-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Introduce el ID de tu Pixel de Meta para rastrear conversiones. 
                El pixel solo se cargara si el usuario acepta <strong>todas las cookies</strong>.
              </p>
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Eventos trackeados automaticamente:</strong>
              </p>
              <ul className="text-xs text-blue-600 mt-1 space-y-1 list-disc list-inside">
                <li>PageView — al cargar cualquier pagina</li>
                <li>ViewContent — al ver paginas de contenido</li>
                <li>InitiateCheckout — al hacer clic en "Comprar Entradas"</li>
                <li>Lead — al suscribirse a la newsletter</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Google Analytics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#50c878]" />
              Google Analytics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="googleAnalyticsId">ID de Google Analytics (GA4)</Label>
              <Input
                id="googleAnalyticsId"
                value={settings?.googleAnalyticsId || ''}
                onChange={(e) => handleChange('googleAnalyticsId', e.target.value)}
                placeholder="G-XXXXXXXXXX"
                className="mt-1"
                data-testid="google-analytics-id-input"
              />
              <p className="text-xs text-gray-500 mt-2">
                Introduce tu ID de medicion GA4. El script solo se cargara si el usuario acepta <strong>todas las cookies</strong>.
                Se trackean automaticamente los cambios de ruta (SPA page views).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#50c878]" />
              Redes Sociales
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="facebook">Facebook</Label>
              <Input
                id="facebook"
                value={settings?.socialLinks?.facebook || ''}
                onChange={(e) => handleSocialChange('facebook', e.target.value)}
                placeholder="https://facebook.com/..."
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="twitter">Twitter / X</Label>
              <Input
                id="twitter"
                value={settings?.socialLinks?.twitter || ''}
                onChange={(e) => handleSocialChange('twitter', e.target.value)}
                placeholder="https://twitter.com/..."
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="instagram">Instagram</Label>
              <Input
                id="instagram"
                value={settings?.socialLinks?.instagram || ''}
                onChange={(e) => handleSocialChange('instagram', e.target.value)}
                placeholder="https://instagram.com/..."
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
